/**
 * @class Component
 */

import * as React from "react";
import { IComponent } from "./interfaces/Component";
import { components } from "./definitions";
import {
  Rect,
  Text,
  Group,
  Image as ReactKonvaImage,
  Circle
} from "react-konva";
import { KonvaEventObject, Text as KonvaText, Group as KonvaGroup } from "konva";
import Connector from "./Connector";
import { IPort, IComponentDefintion } from "./interfaces/ComponentDefinition";
import { componentWidth, componentHeight, startComponentId } from "./utilities";

export type Props = {
  component: IComponent;
  selectedComponentId: string | null;
  newConnectionInProgress: boolean;
  onComponentMousedOver(component: IComponent, x: number, y: number): void;
  onComponentMousedOut(component: IComponent): void;
  onComponentMoved(component: IComponent, x: number, y: number): void;
  onComponentMoveFinished(component: IComponent): void;
  onConnectionStart(component: IComponent, port: IPort): void;
  onComponentDeleted(component: IComponent): void;
  onComponentSelected(component: IComponent | null): void;
};

export type State = {
  imageLoaded: boolean;
  closeImageLoaded: boolean;
  infoImageLoaded: boolean;
  mouseIsOver: boolean;
};

const Options = {
  componentWidth: componentWidth,
  componentHeight: componentHeight,
  iconWidth: 35,
  iconHeight: 25
};

export default class Component extends React.Component<Props, State> {
  text: any;
  image: HTMLImageElement;
  closeImage: HTMLImageElement;
  infoImage: HTMLImageElement;
  definition: IComponentDefintion | undefined;
  state = {
    imageLoaded: false,
    closeImageLoaded: false,
    infoImageLoaded: false,
    mouseIsOver: false
  };
  componentMoved = (ev: KonvaEventObject<DragEvent>) => {
    const x = ev.target.getAttr("x");
    const y = ev.target.getAttr("y");
    this.props.onComponentMoved(this.props.component, x, y);
  };
  componentMoveFinished = (ev: KonvaEventObject<DragEvent>) => {
    this.props.onComponentMoveFinished(this.props.component);
  };
  onPortHovered = (port: IPort) => {
    this.props.onConnectionStart(this.props.component, port);
  };
  onText = (text: any) => {
    if (!this.text) {
      this.text = text;
    }
    if (text) {
      const width = (this.text as KonvaText).getWidth();
      /* (this.text as KonvaText).offsetX(width / 2);
      (this.text as KonvaText).moveToTop(); */
      const padding = Math.abs((text.height() - (componentHeight / 2)) / 2);
      text.y(this.props.component.y + padding);
      // text.x(this.props.component.x);
      // console.info('onText', text.text(), text.height(), this.props.component, padding);
    }
  };

  componentDidMount() {
    // definition
    const { component } = this.props;
    this.definition = components.find(
      (c: IComponentDefintion) => component.graphComponentId === c.graphComponentId
    );
    this.closeImage = new Image(Options.iconWidth);
    this.closeImage.onload = () => {
      this.setState({ closeImageLoaded: true });
    };
    this.closeImage.src = `/assets/images/close-white.svg`;

    this.infoImage = new Image(Options.iconWidth);
    this.infoImage.onload = () => {
      this.setState({ infoImageLoaded: true });
    };
    this.infoImage.src = `/assets/images/info-white.svg`;

    // image loading
    if (this.definition) {
      this.image = new Image(Options.iconWidth);
      this.image.onload = () => {
        this.setState({ imageLoaded: true });
      };
      this.image.src = this.definition.imgSrc;
    }
  }

  componentDeleted = () => {
    this.props.onComponentDeleted(this.props.component);
  };

  componentMousedOver = (event: any) => {
    event.cancelBubble = true;
    this.setState({ mouseIsOver: true });
    this.props.onComponentMousedOver(this.props.component, 0, 0);
  };

  componentMousedOut = (event: any) => {
    event.cancelBubble = true;
    this.setState({ mouseIsOver: false });
    this.props.onComponentMousedOut(this.props.component);
  };

  render() {
    const { component, newConnectionInProgress, selectedComponentId } = this.props;
    const { definition } = this;
    if (!definition) {
      return null;
    }
    const width = Options.componentWidth;
    const height = Options.componentHeight;
    const connectorX = component.x + width / 2;
    const connectorY = component.y;
    const selected = selectedComponentId === this.props.component.id;
    
    let showPeripherals = this.state.mouseIsOver;
    if (newConnectionInProgress) {
      showPeripherals = false; // selected && this.state.mouseIsOver;
    };
    
    return (
      <React.Fragment>
        <Group
          name={component.id}
          onDragMove={this.componentMoved}
          onDragEnd={this.componentMoveFinished}
          onMouseOver={this.componentMousedOver}
          onMouseOut={this.componentMousedOut}
          ref={(node: KonvaGroup) => {
            // deal with cursors
            // node.container().style.cursor = '';
          }}
        >
          <Rect
            draggable
            cornerRadius={5}
            name={component.id}
            x={component.x}
            y={component.y}
            height={height}
            width={width}
            fill={definition.metadata["icon-color"]}
            offsetX={width / 2}
            offsetY={height / 2}
            shadowBlur={20}
            shadowOpacity={0.4}
          />
          {this.state.imageLoaded && (
            <ReactKonvaImage
              image={this.image}
              x={component.x}
              y={component.y - 45}
              width={45}
              height={45}
              offsetX={23}
              listening={false}
              opacity={1}
            />
          )}
          <Text
            width={componentWidth - 14}
            align="center"
            fontSize={14}
            lineHeight={1.3}
            fill="white"
            x={component.x}
            offsetX={componentWidth / 2 - 7}
            ref={(text: KonvaText) => {
              if (text === null) return;
              const padding = Math.abs((text.height() - (componentHeight / 2)) / 2);
              text.y(this.props.component.y + padding);
            }}
            text={definition.title}
            listening={false}
          />
          <Group
            onClick={this.componentDeleted}
            x={component.x - Options.componentWidth / 2}
            y={component.y - Options.componentHeight / 2}
            visible={showPeripherals && component.id !== startComponentId}
          >
            <Circle
              fill="red"
              radius={12}
              shadowBlur={10}
              shadowOpacity={0.5}
              stroke="white"
              strokeWidth={1}
            />
            {this.state.closeImageLoaded && (
              <ReactKonvaImage
                image={this.closeImage}
                offsetX={5}
                offsetY={5}
                width={10}
                height={10}
              />
            )}
          </Group>
          <Group
            onClick={() => this.props.onComponentSelected(component)}
            x={component.x + Options.componentWidth / 2}
            y={component.y - Options.componentHeight / 2}
          >
            <Circle
              fill="orange"
              radius={12}
              shadowBlur={10}
              shadowOpacity={0.5}
              stroke="white"
              strokeWidth={1}
            />
            {this.state.infoImageLoaded && (
              <ReactKonvaImage
                image={this.infoImage}
                offsetX={6}
                offsetY={6}
                width={12}
                height={12}
              />
            )}
          </Group>
          <Connector
            x={connectorX}
            y={connectorY}
            component={component}
            selectedComponentId={selectedComponentId}
            mouseIsOver={this.state.mouseIsOver}
            newConnectionInProgress={newConnectionInProgress}
            onPortHovered={this.onPortHovered}
          />
        </Group>
      </React.Fragment>
    );
  }
}
